import type { NavMonthlyPoint } from '@nav/shared';
import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import { EM_DASH, formatPctPlain } from '@/lib/format';
import { BRAND, EDITORIAL_SHADOW } from '../palette';
import { SFL_PRODUCTS, type ComboFilter, type ComboMetric } from '../sflProducts';

/**
 * Dual-axis combo, product filter (SFL IDR / SFL Dollar / Both) crossed with an
 * independent, multi-select metric filter: MoM, 3-Bulan, YoY. Any combination
 * of the three metrics can be shown at once — the user picks the comparison
 * that answers their question, rather than being handed two fixed charts.
 *
 * MoM and 3-Bulan render as bars on the left axis (both are short/medium-term
 * growth *rates*, same units, naturally compared side by side); YoY renders as
 * a line, on its own right axis when at least one bar metric is also showing,
 * or on the left axis by itself when it's the only metric selected.
 *
 * Colours are the user-specified brand set for this chart (not brand red, and
 * not the generic `CHART` palette used elsewhere — this chart has its own).
 */
const IDR_MOM = '#113052';
const IDR_3M = '#0f6fc6';
const IDR_YOY = '#0bd0d9';
const USD_MOM = '#d68f00';
const USD_3M = '#ffc000';
const USD_YOY = '#ffff00';

/**
 * Picks black or white text for a swatch colour by WCAG relative luminance, so
 * a callout badge stays legible no matter how light or dark that series'
 * colour is (some of the six above are near-black, one is pure yellow).
 */
function contrastText(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.5 ? '#1A1919' : '#FFFFFF';
}

const FILTERS: { id: ComboFilter; label: string }[] = [
  { id: 'SFFR', label: 'SFL IDR' },
  { id: 'SFFD', label: 'SFL Dollar' },
  { id: 'both', label: 'Both' },
];

const METRICS: { id: ComboMetric; label: string }[] = [
  { id: 'mom', label: 'MoM' },
  { id: 'threeMonth', label: '3 Bulan' },
  { id: 'yoy', label: 'YoY' },
];

interface ComboRow {
  period: string;
  label: string;
  momIdr?: number;
  threeMonthIdr?: number;
  yoyIdr?: number;
  momUsd?: number;
  threeMonthUsd?: number;
  yoyUsd?: number;
}

function alignSeries(idr: NavMonthlyPoint[], usd: NavMonthlyPoint[]): ComboRow[] {
  const idrBy = new Map(idr.map((p) => [p.period, p]));
  const usdBy = new Map(usd.map((p) => [p.period, p]));
  const periods = [...new Set([...idr.map((p) => p.period), ...usd.map((p) => p.period)])].sort();
  return periods.map((period) => {
    const a = idrBy.get(period);
    const b = usdBy.get(period);
    const row: ComboRow = {
      period,
      label: a?.label ?? b?.label ?? period,
    };
    if (a?.momPct != null) row.momIdr = a.momPct;
    if (a?.threeMonthPct != null) row.threeMonthIdr = a.threeMonthPct;
    if (a?.yoyPct != null) row.yoyIdr = a.yoyPct;
    if (b?.momPct != null) row.momUsd = b.momPct;
    if (b?.threeMonthPct != null) row.threeMonthUsd = b.threeMonthPct;
    if (b?.yoyPct != null) row.yoyUsd = b.yoyPct;
    return row;
  });
}

function PillGroup<T extends string>({
  ariaLabel,
  items,
  isActive,
  onToggle,
}: {
  ariaLabel: string;
  items: { id: T; label: string }[];
  isActive: (id: T) => boolean;
  onToggle: (id: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        gap: 2,
        padding: 4,
        background: BRAND.track,
        border: `1px solid ${BRAND.panelBorder}`,
        borderRadius: 10,
      }}
    >
      {items.map((item) => {
        const active = isActive(item.id);
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(item.id)}
            style={{
              padding: '7px 12px',
              borderRadius: 7,
              fontSize: 11,
              fontWeight: active ? 600 : 500,
              border: 'none',
              cursor: 'pointer',
              background: active ? BRAND.panel : 'transparent',
              color: active ? BRAND.red : BRAND.textDim,
              boxShadow: active ? `inset 0 0 0 1px rgba(234,27,26,0.30), ${EDITORIAL_SHADOW}` : 'none',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterPills({ value, onChange }: { value: ComboFilter; onChange: (next: ComboFilter) => void }) {
  return (
    <PillGroup
      ariaLabel="Filter produk pada grafik pertumbuhan"
      items={FILTERS}
      isActive={(id) => value === id}
      onToggle={onChange}
    />
  );
}

/** Multi-select: toggling the last remaining metric off is a no-op — the chart always shows at least one. */
function MetricPills({ value, onChange }: { value: ComboMetric[]; onChange: (next: ComboMetric[]) => void }) {
  return (
    <PillGroup
      ariaLabel="Pilih metrik pertumbuhan yang ditampilkan (bisa lebih dari satu)"
      items={METRICS}
      isActive={(id) => value.includes(id)}
      onToggle={(id) => {
        if (value.includes(id)) {
          if (value.length === 1) return;
          onChange(value.filter((m) => m !== id));
        } else {
          onChange([...value, id]);
        }
      }}
    />
  );
}

/**
 * Series colour is shown as a swatch dot, never as the text colour — some of
 * this chart's six colours (near-black navy, pure yellow) are unreadable as
 * text against a dark tooltip, so name/value always render in a fixed light
 * colour and only the dot carries the colour-code.
 */
function ComboTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name?: string; value?: number; color?: string }[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="px-3.5 py-3 min-w-[170px]"
      style={{ background: '#1A1919', borderRadius: 8, border: '1px solid #1A1919' }}
    >
      <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
        {label}
      </p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center gap-2 text-xs tabular-nums" style={{ marginTop: 5 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: item.color ?? '#FFFFFF',
              flexShrink: 0,
            }}
          />
          <span style={{ color: 'rgba(255,255,255,0.75)' }}>{item.name}:</span>
          <span style={{ color: '#FFFFFF', fontWeight: 700 }}>
            {typeof item.value === 'number' ? formatPctPlain(item.value) : EM_DASH}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Uppercase strapline above the chart, e.g. "MoM % (bar) · YoY % (line)" — reflects only what's actually plotted. */
function metricSummary(metrics: ComboMetric[]): string {
  const parts: string[] = [];
  if (metrics.includes('mom')) parts.push('MoM % (bar)');
  if (metrics.includes('threeMonth')) parts.push('3 Bulan % (bar)');
  if (metrics.includes('yoy')) parts.push('YoY % (line)');
  return parts.join(' · ');
}

/** Left-axis label: names whichever bar metric(s) are on it, or borrows YoY when it's the only series at all. */
function barAxisLabel(showMom: boolean, show3m: boolean, showYoy: boolean): string {
  if (showMom && show3m) return 'Growth %';
  if (show3m) return '3 Bulan %';
  if (showMom) return 'MoM %';
  return showYoy ? 'YoY %' : '%';
}

/** Index of the most recent row that actually has a value for this field, or -1 if none does. */
function lastIndexWithValue(rows: ComboRow[], field: keyof ComboRow): number {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i]?.[field] != null) return i;
  }
  return -1;
}

interface LabelRenderProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string | (number | string)[];
  index?: number;
}

/**
 * Callout badge for exactly one point per series: its most recent value. Every
 * other point stays plain — labelling all ~30 months at once would bury the
 * chart in text, but the single current reading is exactly what a viewer
 * scanning the chart wants without having to hover.
 */
function makeEndLabel(color: string, lastIndex: number) {
  return function EndLabel(props: LabelRenderProps) {
    const { index } = props;
    if (index !== lastIndex || Array.isArray(props.value)) return null;
    const x = props.x != null ? Number(props.x) : null;
    const y = props.y != null ? Number(props.y) : null;
    const value = props.value != null ? Number(props.value) : null;
    if (x == null || y == null || value == null || Number.isNaN(value)) return null;

    const width = props.width != null ? Number(props.width) : null;
    const height = props.height != null ? Number(props.height) : null;
    const isBar = width != null && height != null;
    const cx = isBar ? x + width / 2 : x;
    const cy = isBar ? (value < 0 ? y + height + 15 : y - 11) : y - 14;

    const text = formatPctPlain(value);
    const boxWidth = text.length * 6.4 + 12;
    const textColor = contrastText(color);

    return (
      <g>
        <rect x={cx - boxWidth / 2} y={cy - 9} width={boxWidth} height={16} rx={4} fill={color} />
        <text
          x={cx}
          y={cy - 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={10}
          fontWeight={700}
          fill={textColor}
        >
          {text}
        </text>
      </g>
    );
  };
}

export function GrowthComboChart({
  idrPoints,
  usdPoints,
  filter,
  onFilterChange,
  metrics,
  onMetricsChange,
  isLoading,
  error,
  onRetry,
}: {
  idrPoints: NavMonthlyPoint[];
  usdPoints: NavMonthlyPoint[];
  filter: ComboFilter;
  onFilterChange: (next: ComboFilter) => void;
  metrics: ComboMetric[];
  onMetricsChange: (next: ComboMetric[]) => void;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const rows = useMemo(() => alignSeries(idrPoints, usdPoints), [idrPoints, usdPoints]);
  const showIdr = filter === 'SFFR' || filter === 'both';
  const showUsd = filter === 'SFFD' || filter === 'both';
  const showMom = metrics.includes('mom');
  const show3m = metrics.includes('threeMonth');
  const showYoy = metrics.includes('yoy');
  const hasAnyBar = showMom || show3m;
  // YoY gets its own right axis only when it shares the chart with a bar metric.
  const yoyAxisId = hasAnyBar ? 'yoy' : 'mom';

  const fieldHasData = (field: 'momPct' | 'threeMonthPct' | 'yoyPct') =>
    (showIdr && idrPoints.some((p) => p[field] != null)) || (showUsd && usdPoints.some((p) => p[field] != null));
  const hasVisible =
    (showMom && fieldHasData('momPct')) ||
    (show3m && fieldHasData('threeMonthPct')) ||
    (showYoy && fieldHasData('yoyPct'));

  const header = (
    <div className="px-7 pt-6 pb-2 flex items-center justify-between gap-3 flex-wrap">
      <p
        className="uppercase"
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: BRAND.textFaint,
          fontFamily: 'var(--font-sans)',
        }}
      >
        {metricSummary(metrics)}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <MetricPills value={metrics} onChange={onMetricsChange} />
        <FilterPills value={filter} onChange={onFilterChange} />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <LoadingRegion label="Memuat grafik pertumbuhan" className="px-7 pt-4 pb-7">
          <Skeleton height={260} radius={10} />
        </LoadingRegion>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <ErrorState error={error} onRetry={onRetry} compact />
      </Card>
    );
  }

  if (!hasVisible) {
    return (
      <Card>
        {header}
        <EmptyState message="Belum ada data pertumbuhan untuk kombinasi filter ini." compact />
      </Card>
    );
  }

  const idrName = SFL_PRODUCTS[0]!.shortName;
  const usdName = SFL_PRODUCTS[1]!.shortName;

  return (
    <Card>
      {header}
      <div style={{ height: 280, padding: '4px 8px 16px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(27,36,54,0.08)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#6B7488' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="mom"
              tick={{ fontSize: 10, fill: '#6B7488' }}
              tickLine={false}
              axisLine={false}
              width={44}
              tickFormatter={(v: number) => `${v}%`}
              label={{
                value: barAxisLabel(showMom, show3m, showYoy),
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 10, fill: '#8A93A6' },
              }}
            />
            {hasAnyBar && showYoy ? (
              <YAxis
                yAxisId="yoy"
                orientation="right"
                tick={{ fontSize: 10, fill: '#6B7488' }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={(v: number) => `${v}%`}
                label={{
                  value: 'YoY %',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 10, fill: '#8A93A6' },
                }}
              />
            ) : null}
            <Tooltip content={<ComboTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, fontFamily: 'Arial, sans-serif' }} />
            {showMom && showIdr ? (
              <Bar
                yAxisId="mom"
                dataKey="momIdr"
                name={`${idrName} MoM`}
                fill={IDR_MOM}
                maxBarSize={filter === 'both' ? 18 : 32}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList dataKey="momIdr" content={makeEndLabel(IDR_MOM, lastIndexWithValue(rows, 'momIdr'))} />
              </Bar>
            ) : null}
            {showMom && showUsd ? (
              <Bar
                yAxisId="mom"
                dataKey="momUsd"
                name={`${usdName} MoM`}
                fill={USD_MOM}
                maxBarSize={filter === 'both' ? 18 : 32}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList dataKey="momUsd" content={makeEndLabel(USD_MOM, lastIndexWithValue(rows, 'momUsd'))} />
              </Bar>
            ) : null}
            {show3m && showIdr ? (
              <Bar
                yAxisId="mom"
                dataKey="threeMonthIdr"
                name={`${idrName} 3 Bulan`}
                fill={IDR_3M}
                maxBarSize={filter === 'both' ? 18 : 32}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="threeMonthIdr"
                  content={makeEndLabel(IDR_3M, lastIndexWithValue(rows, 'threeMonthIdr'))}
                />
              </Bar>
            ) : null}
            {show3m && showUsd ? (
              <Bar
                yAxisId="mom"
                dataKey="threeMonthUsd"
                name={`${usdName} 3 Bulan`}
                fill={USD_3M}
                maxBarSize={filter === 'both' ? 18 : 32}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="threeMonthUsd"
                  content={makeEndLabel(USD_3M, lastIndexWithValue(rows, 'threeMonthUsd'))}
                />
              </Bar>
            ) : null}
            {showYoy && showIdr ? (
              <Line
                yAxisId={yoyAxisId}
                type="monotone"
                dataKey="yoyIdr"
                name={`${idrName} YoY`}
                stroke={IDR_YOY}
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#fff', stroke: IDR_YOY, strokeWidth: 2 }}
                connectNulls
                isAnimationActive={false}
              >
                <LabelList dataKey="yoyIdr" content={makeEndLabel(IDR_YOY, lastIndexWithValue(rows, 'yoyIdr'))} />
              </Line>
            ) : null}
            {showYoy && showUsd ? (
              <Line
                yAxisId={yoyAxisId}
                type="monotone"
                dataKey="yoyUsd"
                name={`${usdName} YoY`}
                stroke={USD_YOY}
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#fff', stroke: USD_YOY, strokeWidth: 2 }}
                connectNulls
                isAnimationActive={false}
              >
                <LabelList dataKey="yoyUsd" content={makeEndLabel(USD_YOY, lastIndexWithValue(rows, 'yoyUsd'))} />
              </Line>
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
